import * as React from 'react';
import { DatasetDescriptionData, FieldDescriptionData, Theme } from '../../../shared/api/api';
import { addModifiers } from '../../../shared/helpers';
import { renderPercentValue } from '../DataTableCommon/DataTableCommon';
import { DataTableColumn } from '../DataTableCommon/DataTableCommonTypes';
import { CellContext, Row } from '@tanstack/react-table';
import { ThemeLabel } from '../DataTableCommon/ThemeLabel';

export function getUniqueValues<DataType>(row: Row<DataType>, columnId: string) {
  let value = row.getValue(columnId);
  if (row.getIsGrouped()) {
    const uniqueValues = new Set(row.getLeafRows().map(row => row.getValue(columnId)));
    value = [...uniqueValues].sort().join(', ');
  }
  return value;
}

export function getUniqueThemeValues<DataType>(row: Row<DataType>, columnId: string): Theme[] {
  if (!row.getIsGrouped()) {
    return row.getValue(columnId);
  }

  const uniqueValues = new Map();
  row.getLeafRows().forEach(row => {
    const themes = row.getValue(columnId) as Theme[];
    if (themes?.length > 0) {
      themes.forEach(theme => {
        uniqueValues.set(theme.id, theme);
      });
    }
  });
  return [...uniqueValues.values()];
}

export function getSingleValueOrEmpty<DataType>(row: Row<DataType>, columnId: string) {
  const leafRows = row.getLeafRows();
  if (row.getIsGrouped() && leafRows.length > 1) {
    return undefined;
  }
  // @ts-expect-error dynamic column key not in react-table row.original type
  const originalValue = row.original?.[columnId];
  return originalValue ?? row.getValue(columnId);
}

export function getRegionStatsTableColumns<DataType extends FieldDescriptionData>(
  trackEvent: (event: string) => void,
  isPyramidMultiplierVisible: boolean
): DataTableColumn<DataType>[] {
  return [
    {
      id: 'region',
      name: 'dataExplorer.datasetFields.region',
      accessorFn: (dataset: DataType) => dataset.region,
      active: true,
      display: true,
      type: 'string',
      size: 50,
      cell: rowData => {
        const row = rowData.row;
        return <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>{row.getValue('region')}</div>;
      },
    },
    {
      id: 'delay',
      name: 'dataExplorer.datasetFields.delay',
      accessorFn: (dataset: DataType) => dataset.delay,
      active: true,
      display: true,
      type: 'string',
      size: 50,
      cell: rowData => {
        const row = rowData.row;

        return (
          <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>
            {getUniqueValues(row, 'delay') as React.ReactNode}
          </div>
        );
      },
    },

    {
      id: 'universe',
      name: 'dataExplorer.datasetFields.universe',
      accessorFn: (dataset: DataType) => dataset.universe,
      active: true,
      display: true,
      type: 'percent',
      size: 150,
      cell: props => {
        const row = props.row;
        const columnId = 'universe';

        return (
          <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>
            {getUniqueValues<DataType>(row, columnId) as React.ReactNode}
          </div>
        );
      },
    },
    ...(isPyramidMultiplierVisible
      ? [
          {
            id: 'pyramidMultiplier',
            name: 'dataExplorer.datasetFields.pyramidMultiplier',
            accessorFn: (dataset: DataType) => dataset.pyramidMultiplier,
            active: true,
            display: true,
            type: 'decimal',
            size: 45,
            cell: (rowData: CellContext<DataType, DataType>) => {
              const row = rowData.row;
              return (
                <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>
                  {getUniqueValues<DataType>(row, 'pyramidMultiplier') as React.ReactNode}
                </div>
              );
            },
          },
        ]
      : []),
    {
      id: 'theme',
      name: 'dataExplorer.datasetFields.theme',
      accessorFn: (dataset: DataType) => dataset.themes,
      active: true,
      display: true,
      type: 'string',
      size: 50,
      cell: rowData => {
        const row = rowData.row;

        return (
          <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>
            <ThemeLabel themes={getUniqueThemeValues(row, 'theme')} />
          </div>
        );
      },
    },
    {
      id: 'coverage',
      name: 'dataExplorer.datasetFields.coverage',
      accessorFn: (dataset: DataType) => dataset.coverage,

      active: true,
      display: true,
      type: 'string',
      size: 50,
      cell: rowData => {
        const row = rowData.row;
        const isGroupRow = row.getIsGrouped() && row.getLeafRows().length > 1;
        const coverageValue = row.getValue('coverage') ?? row.original?.coverage;
        return (
          <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>
            {isGroupRow ? '' : renderPercentValue(coverageValue as number)}
          </div>
        );
      },
    },
    {
      id: 'dateCoverage',
      name: 'dataExplorer.datasetFields.dateCoverage',
      accessorFn: (dataset: DataType) => dataset.dateCoverage,

      active: true,
      display: true,
      type: 'string',
      size: 50,
      cell: rowData => {
        const row = rowData.row;
        const isGroupRow = row.getIsGrouped() && row.getLeafRows().length > 1;
        const coverageValue = row.getValue('dateCoverage') ?? row.original?.dateCoverage;
        return (
          <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>
            {isGroupRow ? '' : renderPercentValue(coverageValue as number)}
          </div>
        );
      },
    },
    {
      id: 'fieldCount',
      name: 'dataExplorer.datasetFields.fieldCount',
      accessorFn: (dataset: DataType) => (dataset as unknown as DatasetDescriptionData).fieldCount,
      active: true,
      display: true,
      type: 'integer',
      size: 50,
      cell: rowData => {
        const row = rowData.row;
        return (
          <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>
            {getSingleValueOrEmpty<DataType>(row, 'fieldCount')}
          </div>
        );
      },
    },
    {
      id: 'alphaCount',
      name: 'dataExplorer.datasetFields.alphaCount',
      accessorFn: (dataset: DataType) => dataset.alphaCount,

      active: true,
      display: true,
      type: 'integer',
      size: 50,
      cell: rowData => {
        const row = rowData.row;
        return (
          <div className={addModifiers('data-table__cell-content', ['with-wrapping'])}>
            {getSingleValueOrEmpty<DataType>(row, 'alphaCount')}
          </div>
        );
      },
    },
  ];
}
